/** Company hierarchy seed + visibility policy (DEC-ORG-001 / DEC-ORG-002). */

export type OrgChainSide = "delivery" | "back_office";
export type OrgUnitType =
  | "company"
  | "division"
  | "business_unit"
  | "department"
  | "team"
  | "other";

export type OrgChartRank =
  | "employee"
  | "supervisor"
  | "project_manager"
  | "director"
  | "vp"
  | "executive"
  | "unrestricted";

export const ORG_RANK_ORDER: OrgChartRank[] = [
  "employee",
  "supervisor",
  "project_manager",
  "director",
  "vp",
  "executive",
  "unrestricted",
];

export type OrgPerson = {
  id: string;
  name: string;
  title: string;
  rank: OrgChartRank;
  orgUnitId: string;
  supervisorId: string | null;
  chainSide: OrgChainSide;
};

export type OrgUnitNode = {
  id: string;
  name: string;
  code: string;
  type: OrgUnitType;
  parentId: string | null;
  chainSide: OrgChainSide;
  assignedVpId: string | null;
  assignedDirectorId: string | null;
};

export type VisibilityPolicyRow = {
  roleId: string;
  label: string;
  ceiling: OrgChartRank;
  lockedFull?: boolean;
};

const STORAGE_POLICY = "govsuite.demo.orgchart.visibility";

export const SEED_ORG_UNITS: OrgUnitNode[] = [
  {
    id: "ou-hfs",
    name: "Harbor Federal Services",
    code: "HFS",
    type: "company",
    parentId: null,
    chainSide: "delivery",
    assignedVpId: null,
    assignedDirectorId: null,
  },
  {
    id: "ou-ops",
    name: "Operations",
    code: "OPS",
    type: "division",
    parentId: "ou-hfs",
    chainSide: "delivery",
    assignedVpId: "emp-vp-del",
    assignedDirectorId: null,
  },
  {
    id: "ou-bu-fedciv",
    name: "Federal Civilian",
    code: "BU-FC",
    type: "business_unit",
    parentId: "ou-ops",
    chainSide: "delivery",
    assignedVpId: "emp-vp-del",
    assignedDirectorId: "emp-dir-fc",
  },
  {
    id: "ou-bu-dod",
    name: "DoD Mission Support",
    code: "BU-DOD",
    type: "business_unit",
    parentId: "ou-ops",
    chainSide: "delivery",
    assignedVpId: "emp-vp-del",
    assignedDirectorId: "emp-dir-dod",
  },
  {
    id: "ou-eng",
    name: "Engineering",
    code: "ENG",
    type: "department",
    parentId: "ou-bu-fedciv",
    chainSide: "delivery",
    assignedVpId: null,
    assignedDirectorId: "emp-dir-fc",
  },
  {
    id: "ou-fin",
    name: "Finance",
    code: "FIN",
    type: "division",
    parentId: "ou-hfs",
    chainSide: "back_office",
    assignedVpId: "emp-vp-fin",
    assignedDirectorId: "emp-dir-fin",
  },
  {
    id: "ou-hr",
    name: "Human Resources",
    code: "HR",
    type: "division",
    parentId: "ou-hfs",
    chainSide: "back_office",
    assignedVpId: null,
    assignedDirectorId: "emp-dir-hr",
  },
  {
    id: "ou-contracts",
    name: "Contracts",
    code: "CON",
    type: "department",
    parentId: "ou-hfs",
    chainSide: "back_office",
    assignedVpId: null,
    assignedDirectorId: null,
  },
];

export const SEED_ORG_PEOPLE: OrgPerson[] = [
  {
    id: "emp-vp-del",
    name: "Casey Vogel",
    title: "VP, Delivery",
    rank: "vp",
    orgUnitId: "ou-ops",
    supervisorId: null,
    chainSide: "delivery",
  },
  {
    id: "emp-dir-fc",
    name: "Taylor Brooks",
    title: "Director, Federal Civilian",
    rank: "director",
    orgUnitId: "ou-bu-fedciv",
    supervisorId: "emp-vp-del",
    chainSide: "delivery",
  },
  {
    id: "emp-dir-dod",
    name: "Quinn Avery",
    title: "Director, DoD Mission Support",
    rank: "director",
    orgUnitId: "ou-bu-dod",
    supervisorId: "emp-vp-del",
    chainSide: "delivery",
  },
  {
    id: "emp-ortiz",
    name: "Jordan Ortiz",
    title: "Project Manager",
    rank: "project_manager",
    orgUnitId: "ou-eng",
    supervisorId: "emp-dir-fc",
    chainSide: "delivery",
  },
  {
    id: "emp-sup-a",
    name: "Riley Shaw",
    title: "Engineering Supervisor",
    rank: "supervisor",
    orgUnitId: "ou-eng",
    supervisorId: "emp-ortiz",
    chainSide: "delivery",
  },
  {
    id: "emp-chen",
    name: "Avery Chen",
    title: "Systems Engineer",
    rank: "employee",
    orgUnitId: "ou-eng",
    supervisorId: "emp-sup-a",
    chainSide: "delivery",
  },
  {
    id: "emp-blake",
    name: "Morgan Blake",
    title: "Analyst",
    rank: "employee",
    orgUnitId: "ou-eng",
    supervisorId: "emp-sup-a",
    chainSide: "delivery",
  },
  {
    id: "emp-vp-fin",
    name: "Alex Rivera",
    title: "VP, Finance",
    rank: "vp",
    orgUnitId: "ou-fin",
    supervisorId: null,
    chainSide: "back_office",
  },
  {
    id: "emp-dir-fin",
    name: "Jamie Cole",
    title: "Director, Accounting",
    rank: "director",
    orgUnitId: "ou-fin",
    supervisorId: "emp-vp-fin",
    chainSide: "back_office",
  },
  {
    id: "emp-fin-1",
    name: "Sam Lee",
    title: "Staff Accountant",
    rank: "employee",
    orgUnitId: "ou-fin",
    supervisorId: "emp-dir-fin",
    chainSide: "back_office",
  },
  {
    id: "emp-dir-hr",
    name: "Riley Chen",
    title: "Director, HR",
    rank: "director",
    orgUnitId: "ou-hr",
    supervisorId: null,
    chainSide: "back_office",
  },
  {
    id: "emp-hr-1",
    name: "Pat Morgan",
    title: "HR Analyst",
    rank: "employee",
    orgUnitId: "ou-hr",
    supervisorId: "emp-dir-hr",
    chainSide: "back_office",
  },
  {
    id: "emp-patel",
    name: "Sam Patel",
    title: "Contract Specialist",
    rank: "employee",
    orgUnitId: "ou-contracts",
    supervisorId: null,
    chainSide: "back_office",
  },
];

export const DEFAULT_VISIBILITY: VisibilityPolicyRow[] = [
  { roleId: "ROLE-EMPLOYEE", label: "Employee", ceiling: "project_manager" },
  { roleId: "ROLE-SUPERVISOR", label: "Supervisor", ceiling: "project_manager" },
  { roleId: "ROLE-PROJECT-MANAGER", label: "Project Manager", ceiling: "unrestricted" },
  { roleId: "ROLE-DIRECTOR", label: "Director", ceiling: "unrestricted" },
  { roleId: "ROLE-VP", label: "VP (Delivery)", ceiling: "unrestricted" },
  { roleId: "ROLE-HR", label: "HR", ceiling: "unrestricted", lockedFull: true },
  { roleId: "ROLE-VP-HR", label: "VP HR", ceiling: "unrestricted", lockedFull: true },
  {
    roleId: "ROLE-COMPANY-ADMIN",
    label: "Company Admin",
    ceiling: "unrestricted",
    lockedFull: true,
  },
  {
    roleId: "ROLE-ADMIN-SECURITY-INTEGRATION",
    label: "Admin / Security",
    ceiling: "unrestricted",
    lockedFull: true,
  },
  {
    roleId: "ROLE-EXECUTIVE-BI-AUDITOR",
    label: "Executive / BI",
    ceiling: "unrestricted",
  },
];

export function loadVisibilityPolicy(): VisibilityPolicyRow[] {
  try {
    const raw = localStorage.getItem(STORAGE_POLICY);
    if (!raw) return DEFAULT_VISIBILITY.map((r) => ({ ...r }));
    const parsed = JSON.parse(raw) as VisibilityPolicyRow[];
    if (!Array.isArray(parsed)) return DEFAULT_VISIBILITY.map((r) => ({ ...r }));
    return DEFAULT_VISIBILITY.map((d) => {
      const hit = parsed.find((p) => p.roleId === d.roleId);
      if (!hit) return { ...d };
      if (d.lockedFull) return { ...d, ceiling: "unrestricted" };
      return { ...d, ceiling: hit.ceiling };
    });
  } catch {
    return DEFAULT_VISIBILITY.map((r) => ({ ...r }));
  }
}

export function saveVisibilityPolicy(rows: VisibilityPolicyRow[]) {
  localStorage.setItem(STORAGE_POLICY, JSON.stringify(rows));
}

export function rankIndex(r: OrgChartRank): number {
  return ORG_RANK_ORDER.indexOf(r);
}

/** Highest ceiling among preview roles (unrestricted wins). */
export function effectiveCeiling(
  roleIds: string[],
  policy: VisibilityPolicyRow[],
): OrgChartRank {
  let best: OrgChartRank = "employee";
  for (const id of roleIds) {
    const row = policy.find((p) => p.roleId === id);
    const c = row?.ceiling ?? "project_manager";
    if (rankIndex(c) > rankIndex(best)) best = c;
  }
  // Roles with no policy row: treat PM+ style named hats
  for (const id of roleIds) {
    if (
      id === "ROLE-CONTRACTS-SUBCONTRACTS" ||
      id === "ROLE-FINANCE-ACCOUNTING" ||
      id === "ROLE-CGO"
    ) {
      if (rankIndex("unrestricted") > rankIndex(best)) best = "unrestricted";
    }
  }
  return best;
}

export function personById(id: string | null | undefined): OrgPerson | undefined {
  if (!id) return undefined;
  return SEED_ORG_PEOPLE.find((p) => p.id === id);
}

export function unitById(id: string): OrgUnitNode | undefined {
  return SEED_ORG_UNITS.find((u) => u.id === id);
}

/** Whether a person's rank is visible under ceiling (own upward chain always kept separately). */
export function rankVisible(personRank: OrgChartRank, ceiling: OrgChartRank): boolean {
  if (ceiling === "unrestricted") return true;
  return rankIndex(personRank) <= rankIndex(ceiling);
}

export function upwardChainIds(personId: string): Set<string> {
  const ids = new Set<string>();
  let cur: OrgPerson | undefined = personById(personId);
  while (cur) {
    ids.add(cur.id);
    cur = cur.supervisorId ? personById(cur.supervisorId) : undefined;
  }
  return ids;
}

export type TreeNode = {
  kind: "unit" | "person";
  id: string;
  label: string;
  sublabel: string;
  rank?: OrgChartRank;
  unit?: OrgUnitNode;
  person?: OrgPerson;
  children: TreeNode[];
};

export function buildOrgTree(side: OrgChainSide | "all"): TreeNode[] {
  const units = SEED_ORG_UNITS.filter(
    (u) => side === "all" || u.chainSide === side || u.type === "company",
  );
  const people = SEED_ORG_PEOPLE.filter((p) => side === "all" || p.chainSide === side);

  function childrenOfUnit(unitId: string): TreeNode[] {
    const childUnits = units
      .filter((u) => u.parentId === unitId)
      .map((u) => unitNode(u));
    const childPeople = people
      .filter((p) => p.orgUnitId === unitId)
      .map((p) => personNode(p));
    // Nest people under their supervisors when supervisor is in same unit
    const byId = new Map(childPeople.map((n) => [n.id, n]));
    const roots: TreeNode[] = [];
    for (const n of childPeople) {
      const sup = n.person?.supervisorId;
      if (sup && byId.has(sup)) {
        byId.get(sup)!.children.push(n);
      } else {
        roots.push(n);
      }
    }
    return [...childUnits, ...roots];
  }

  function unitNode(u: OrgUnitNode): TreeNode {
    const vp = personById(u.assignedVpId);
    const dir = personById(u.assignedDirectorId);
    const leadBits = [
      vp ? `VP: ${vp.name}` : null,
      dir ? `Dir: ${dir.name}` : null,
    ].filter(Boolean);
    return {
      kind: "unit",
      id: u.id,
      label: `${u.name} (${u.code})`,
      sublabel: [u.type.replace("_", " "), ...leadBits].join(" · "),
      unit: u,
      children: childrenOfUnit(u.id),
    };
  }

  function personNode(p: OrgPerson): TreeNode {
    return {
      kind: "person",
      id: p.id,
      label: p.name,
      sublabel: `${p.title} · ${p.rank}`,
      rank: p.rank,
      person: p,
      children: [],
    };
  }

  const roots = units.filter((u) => !u.parentId || !units.some((x) => x.id === u.parentId));
  // Prefer company root
  const company = units.find((u) => u.type === "company");
  if (company) return [unitNode(company)];
  return roots.map(unitNode);
}

/** Prune tree by visibility; keep upward chain of focusPersonId. */
export function filterTreeByVisibility(
  nodes: TreeNode[],
  ceiling: OrgChartRank,
  keepPersonIds: Set<string>,
): TreeNode[] {
  const out: TreeNode[] = [];
  for (const n of nodes) {
    if (n.kind === "person") {
      const keep =
        keepPersonIds.has(n.id) ||
        (n.rank != null && rankVisible(n.rank, ceiling));
      if (!keep) continue;
      out.push({
        ...n,
        children: filterTreeByVisibility(n.children, ceiling, keepPersonIds),
      });
      continue;
    }
    const kids = filterTreeByVisibility(n.children, ceiling, keepPersonIds);
    // Keep unit if it has visible descendants or has leadership within ceiling
    const vp = personById(n.unit?.assignedVpId);
    const dir = personById(n.unit?.assignedDirectorId);
    const leadOk =
      ceiling === "unrestricted" ||
      (vp != null && rankVisible(vp.rank, ceiling)) ||
      (dir != null && rankVisible(dir.rank, ceiling)) ||
      kids.length > 0;
    if (!leadOk && kids.length === 0) continue;
    out.push({ ...n, children: kids });
  }
  return out;
}
